import { createElement as rc, useState } from 'react';
import PropTypes from 'prop-types';
import useFormControl from '../../hooks/useFormControl';
import { TextInput, styled } from 'lib_ui-primitives';
import FormField from './FormField';
const StyledTextInput = styled(TextInput)`
    flex-grow: 1;
`;

const _p = {
    useFormControl
};

/**
 * Decimal text, with an optional sign, fractional part and exponent.
 *
 * Capturing the pieces separately is what makes the question below answerable WITHOUT converting.
 */
const DECIMAL_TEXT = /^([+-]?)(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/;

/**
 * Whether this text denotes a value with a real fractional part.
 *
 * Decided on the DIGITS, because converting first destroys the evidence: a double keeps about 15
 * significant digits, so `Number('1.0000000000000001')` is exactly 1.
 *
 * Exponents are part of the calculation rather than an exemption from it. Matching a decimal
 * point followed by a non-zero digit and skipping exponent forms looks safe — '1.5e1' really is
 * 15 — but '1.00000000000000001e0' is the same fraction wearing an exponent and walks straight
 * through. So the point is MOVED instead: `remaining` is how many digits still sit to its right
 * once the exponent has shifted it, and a non-zero digit among them means a fractional part in
 * any notation.
 *
 * The same reasoning lives in `_integerFromText` in lib/order-domain, which is what finally
 * refuses the value; this is the half that keeps it legible long enough to get there.
 */
function hasFractionalPart(text) {
    const match = DECIMAL_TEXT.exec(String(text).trim());
    if (!match) return false;
    const [, , whole = '', fraction = '', exponent] = match;
    const remaining = fraction.length - (exponent ? Number(exponent) : 0);
    if (remaining <= 0) return false;
    const digits = whole + fraction;
    // A point further left than the digits go is padded with leading zeros, which cannot make
    // the tail non-zero — so the whole string is the tail.
    const tail = remaining >= digits.length ? digits : digits.slice(digits.length - remaining);
    return /[1-9]/.test(tail);
}

function Integer(props) {
    const {
        autoComplete = 'off',
        hNode: {
            id,
            min = Number.MIN_SAFE_INTEGER,
            max = Number.MAX_SAFE_INTEGER,
            maxLength,
            minLength,
            placeholder,
            tooltip,
            pattern,
            propertyName,
            treePosition,
            required = false
        },
        onBlur,
        onFocus
    } = props || { hNode: {} };

    const [active, setActive] = useState(false);
    const { title, value, setValue, isDefaultValue, disabled, errors } = _p.useFormControl(props);

    // What was typed, as a number — not a rounded version of it.
    //
    // This was `+parseFloat(value).toFixed(2)`, which rounded to two decimals on the way into
    // the model. In an Integer field that is never a correction: it cannot make a value whole
    // except by accident, and when it does the accident is silent. A user typing 1.999 had it
    // stored as 2, which then PASSED the integer validation and saved — so the field quietly
    // accepted a fraction and recorded something the user never entered. 1.5 survived, 1.999
    // did not, which is the worst kind of inconsistency to meet as a user.
    //
    // Keeping the entered value means validation sees what was actually typed and can refuse
    // it, which is what #2281 AC 1 asks for and what the error message beside the field claims
    // is happening.
    function onChange(value) {
        if (value === '' || isNaN(value)) return setValue('');
        const asNumber = +value;
        // Converting can destroy the evidence that this is not a whole number.
        //
        // `Number` keeps about 15 significant digits, so '1.0000000000000001' becomes EXACTLY 1
        // — `Number.isInteger` then says yes and the field saves a whole quantity the user never
        // typed, reporting nothing. '1.00000000000000001e0' is the same value in exponent form
        // and has to be caught by the same test, which is why the check decomposes the text
        // rather than looking for a decimal point.
        //
        // Handing the TEXT on in that one case is what lets the validation see the fractional
        // part and refuse it. Anything else is passed on as a number, exactly as before: a plain
        // 1.5 is already non-integer once converted and needs no help, and keeping strings for
        // ordinary input would change the stored type for every other field using this component.
        if (Number.isInteger(asNumber) && hasFractionalPart(value)) return setValue(value);
        setValue(asNumber);
    }

    /**
     * @param {React.FocusEvent<HTMLInputElement>} event
     */
    function handleOnFocus(e) {
        setActive(true);
        e.target.select?.();
        onFocus?.(e);
    }

    function handleOnBlur(e) {
        setActive(false);
        onBlur?.(e);
    }

    // Formatted only when it IS a whole number. `toLocaleString` with maximumFractionDigits 0
    // ROUNDS, so a blurred field holding 1.5 rendered as "2" while the model still held 1.5 —
    // the validation correctly refused the save, and the message "must be an integer" appeared
    // beside a field reading 2. The value was never corrupted; the display contradicted it, and
    // the only way out was to retype. A non-integer is now shown as what it is, so the error and
    // the field agree.
    let _value =
        active || !Number.isInteger(value)
            ? value
            : value.toLocaleString(undefined, {
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
                useGrouping: false
            });

    // previously, we did not display zero values.
    // but for ports or versions, 0 is a valid entry
    if (isDefaultValue) {
        _value = _value === '0' || _value === 0 ? '' : _value;
    }

    // Set the fieldEmpty value so that the label can move over the field if necessary
    const fieldEmpty = _value === '';

    // prettier-ignore
    return rc(FormField, { id, title, tooltip, errors, active, fieldEmpty, required },
        rc(StyledTextInput, {
            id,
            name: propertyName,
            type: active ? 'number' : 'text',
            disabled,
            autoComplete,
            spellCheck: false,
            max,
            maxLength,
            min,
            minLength,
            pattern,
            placeholder,
            value: _value,
            onChange,
            onBlur: handleOnBlur,
            onFocus: handleOnFocus,
            sequence: treePosition.sequence
        })
    );
}

Integer.propTypes = {
    hNode: PropTypes.shape({
        treePosition: PropTypes.shape({
            sequence: PropTypes.number.isRequired
        }).isRequired
    }).isRequired
};

export default Integer;
export const _private = _p;
